import dayjs from "@lib/dayjs";
import { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const generateTimeOptions = (step = 15) => {
  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      const hour = h.toString().padStart(2, "0");
      const minute = m.toString().padStart(2, "0");
      times.push(`${hour}:${minute}`);
    }
  }
  return times;
};

interface CustomTimePickerProps {
  disabled?: boolean;
  value: Dayjs;
  step?: number;
  placeholder: string;
  containerStyle?: string;
  onChange?: (time: string) => void;
}

const CustomTimePicker = ({
  disabled = false,
  value,
  step = 15,
  onChange,
  placeholder,
  containerStyle,
}: CustomTimePickerProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const [selectedTime, setSelectedTime] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const selectedTimeRef = useRef<HTMLDivElement>(null);
  const timeOptions = generateTimeOptions(step);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref?.current?.contains(e.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (time: string) => {
    setSelectedTime(time);
    onChange && onChange(time);
    setShowPicker(false);
  };

  useEffect(() => {
    if (value) {
      const hr = dayjs(value).get("hours").toString().padStart(2, "0");
      const min = dayjs(value).get("minutes").toString().padStart(2, "0");
      setSelectedTime(hr + ":" + min);
    }
  }, [value]);

  // Scroll to selected time when picker opens
  useEffect(() => {
    if (showPicker && selectedTimeRef.current) {
      selectedTimeRef.current.scrollIntoView({ block: "center" });
    }
  }, [showPicker]);

  return (
    <div className="relative" ref={ref}>
      <input
        disabled={disabled}
        type="text"
        readOnly
        value={selectedTime}
        onClick={() => (!disabled ? setShowPicker(!showPicker) : null)}
        className={twMerge(
          `w-16 p-2 border rounded-md ${
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          } font-medium text-xs text-center`,
          containerStyle
        )}
        placeholder={placeholder || ""}
      />
      {showPicker && (
        <div className="absolute z-40 w-max max-h-52 overflow-y-auto no-scrollbar bg-white border rounded-lg shadow-md mt-1">
          {timeOptions.map((time) => (
            <div
              key={time}
              ref={time === selectedTime ? selectedTimeRef : null}
              onClick={() => handleSelect(time)}
              className={twMerge(
                "font-medium px-4 py-2 cursor-pointer text-xs text-center hover:bg-blue-100",
                time === selectedTime ? "bg-blue-500 text-white" : ""
              )}
            >
              {time}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomTimePicker;
